import { Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private languageSubject = new BehaviorSubject<string>('en-GB');
  currentLanguage$ = this.languageSubject.asObservable();

  private readonly STORAGE_KEY = 'globalLanguage';

  private readonly languageMap: Record<string, string> = {
    'en-GB': 'en',
    'en-US': 'en',
    'en': 'en',
    'en-INT': 'en_INT',
    'cs-CZ': 'cs',
    'de-DE': 'de',
    'de-CH': 'de',
    'el-GR': 'el',
    'es-ES': 'es',
    'fr-FR': 'fr',
    'fr-CH': 'fr',
    'it-IT': 'it',
    'it-CH': 'it',
    'ja-JP': 'ja',
    'ko-KR': 'ko',
    'nl-NL': 'nl',
    'pl-PL': 'pl',
    'pt-PT': 'pt',
    'ro-RO': 'ro',
    'ru-RU': 'ru',
    'uk-UA': 'uk',
    'zh-CN': 'cn'
  };
  private readonly supportedUiCodes = Object.keys(this.languageMap);

  constructor(private translate: TranslateService) { }

  private resolveLang(code: string): string {
    return this.languageMap[code] || 'en';
  }

  private normalizeToSupportedUiCode(deviceCode: string | undefined | null): string {
    if (!deviceCode) return 'en-GB';

    const normalized = deviceCode.replace('_', '-');

    if (this.supportedUiCodes.includes(normalized)) {
      return normalized;
    }

    const base = normalized.split('-')[0].toLowerCase();

    const fallbackByBase: Record<string, string> = {
      en: 'en-GB',
      de: 'de-DE',
      fr: 'fr-FR',
      it: 'it-IT',
      nl: 'nl-NL',
      cs: 'cs-CZ',
      pl: 'pl-PL',
      zh: 'zh-CN',
      ko: 'ko-KR',
      ja: 'ja-JP',
      ro: 'ro-RO',
      es: 'es-ES',
      ru: 'ru-RU',
      uk: 'uk-UA',
      el: 'el-GR',
      pt: 'pt-PT'
    };

    return fallbackByBase[base] || 'en-GB';
  }

  async initLanguage(): Promise<void> {
    let stored = localStorage.getItem(this.STORAGE_KEY);
    if (!stored) {
      try {
        const deviceLang = await Device.getLanguageCode();
        stored = this.normalizeToSupportedUiCode(deviceLang.value);
      } catch {
        stored = this.normalizeToSupportedUiCode(navigator.language);
      }
      localStorage.setItem(this.STORAGE_KEY, stored);
    }

    const lang = this.resolveLang(stored);
    this.translate.setDefaultLang('en');
    this.translate.use(lang);
    this.languageSubject.next(stored);
  }

  setLanguage(langCode: string): void {
    localStorage.setItem(this.STORAGE_KEY, langCode);
    const lang = this.resolveLang(langCode);
    this.translate.setDefaultLang('en');
    this.translate.use(lang);
    this.languageSubject.next(langCode);
  }

  getCurrentLanguage(): string {
    return localStorage.getItem(this.STORAGE_KEY) || 'en-GB';
  }
}